/**
 * audio-timeline.js — capture-clock audio for the screen recorder's WebCodecs
 * pipeline. Replaces the AudioContext mixer + MediaStreamTrackProcessor(dest)
 * front-end for that path (the MediaRecorder fallback keeps buildMixer).
 *
 * WHY (Анастасия Горнова, #1443 → #1553, «видео отстаёт от звука», measured
 * 2026-09-02 on the tool itself with ffmpeg reading the output file):
 *
 *   1. The mixer's output carried NO capture timestamps (a MediaStreamDestination
 *      stamps its AudioData by rendered sample count), so the file's audio zero
 *      was «whichever sample we happened to feed first» and the video zero was a
 *      restamped peek frame that still carried the peek's ~150–200 ms delivery
 *      latency. Result: a CONSTANT +210…235 ms video-behind-audio offset in
 *      every recording, before any drift.
 *   2. The mixer AudioContext was suspended on every tab switch by our own
 *      battery pool in timbrica-core.js (the recorder tab is hidden for most
 *      of a recording — the user watches the tab they capture). Each cycle
 *      lost 0.4–2.7 s of audio (prod telemetry, 09-02). Pre-08-20 that was
 *      pure drift; after 08-20 it became silence dropouts of the same length.
 *   3. MediaStreamTrackProcessor's DEFAULT audio buffer is ~13 frames (120 ms,
 *      measured): any main-thread stall longer than that drops audio. 300 ms
 *      stalls once a second lost 20% of the audio (measured); with
 *      maxBufferSize=400 the loss is 0. Prod: 47 289 sub-250 ms silence fills
 *      in 425 sessions since 08-20 — a steady ~0.2–1% loss on busy machines,
 *      which the fill logic turned into a 50…200 ms audio-ahead sawtooth.
 *
 * WHAT (measured, not assumed — tests/scratch probes 2026-09-02, Chrome 152 +
 * Chromium 147):
 *   • Raw capture tracks read through MediaStreamTrackProcessor carry TRUE
 *     capture timestamps: (timestamp − arrival) is constant to ±0.2 ms for
 *     audio and ±0.4 ms for display video. Video frames are in Chromium's
 *     TimeTicks domain (µs since boot); audio frames are in the
 *     performance.now() domain on Chrome 152 (and TimeTicks on 147). The
 *     constant lets each source be bridged onto ONE file clock exactly.
 *   • With that bridge the file's audio and video zeros are the same wall
 *     instant, gaps are placed where the timestamps say they are, and
 *     nothing depends on the AudioContext state or on wall-clock heuristics.
 *
 * HOW: every source (system audio, mic) is read raw with a 4-second buffer;
 * each frame is converted to the file clock (perf ms − origin − T0 − pauses),
 * placed at its exact output position by a 16-tap windowed-sinc resampler
 * whose ratio is servo-corrected by the timestamps (no clicks on clock
 * drift), summed into a ring with per-source gains, soft-limited when two
 * sources overlap, and emitted as f32-planar blocks for the AudioEncoder.
 * The encoder stamps output by sample count, so the emitted stream IS the
 * timeline: it always starts at file time 0 and contains silence for holes.
 *
 * The object mimics the buildMixer() shape the rest of the recorder expects
 * (sysGain/micGain {gain:{value}}, analyser.getFloatTimeDomainData, ctx=null).
 *
 * DENOISE (26.10.2026): a source defined with `denoise` (the microphone) goes
 * through RNNoise between its resampler and the ring — at 48 kHz, which is the
 * only rate the network was trained for, on the file clock. RNNoiseProcessor.
 * openStream() returns sample k of its output for sample k of its input, so a
 * sample lands at the same output index it would have without the network;
 * only its availability lags (≤ 2 frames, 20 ms), and emission waits for the
 * denoised head instead of the resampler's. A gap ≤ 2 s is fed as silence (the
 * network's state stays continuous); a longer one, or a re-anchor, finishes the
 * stream and opens a fresh one. Measured on a fake microphone (Chrome 156,
 * fan / keyboard at 5 and 15 dB SNR): STOI 0.887/0.972/0.801/0.916 against
 * 0.822/0.939/0.757/0.895 for Chrome's own noiseSuppression, clean speech
 * 0.996 — the engine turns the browser's suppressor off when this is on.
 *
 * STEMS (26.10.2026): with `stems`, every source is also kept in a ring of its
 * own (the microphone mono, after the network; the system sound in the output
 * channels), emitted together with the mix by onStems({mic, sys}, n, startUs):
 * one clock for the take and its separate tracks, so they line up in an editor.
 */
(function () {
    'use strict';

    var RING_SEC = 8;           // output ring: how far a source may run ahead of emission
    var TAPS = 16;              // windowed-sinc taps (8 each side)
    var SINC_PH = 1024;         // sub-sample phases in the coefficient table
    var HARD_MS = 40;           // |timestamp − resampler head| beyond this = real discontinuity
    var SERVO_S = 2.0;          // small errors converge over ~2 s of audio
    var MAX_RATIO_DEV = 0.002;  // ±0.2% ratio pull (≈ 2000 ppm) — inaudible, converges fast
    var ALIVE_MS = 1500;        // a source silent for longer stops gating emission
    var CAL_N = 8;              // frames used to bridge a source's clock onto performance.now()
    var MAX_BUFFER = 400;       // MSTP frames ≈ 4 s of 10 ms frames — main-thread stall tolerance
    var PRE_MS = 3000;          // frames kept before start() (for the exact cut at T0)
    var DRAIN_MAX_MS = 5000;    // at Stop: how long the queued tail may take to read (a 3.7 s tail took 1.7 s on a loaded machine)
    var DRAIN_IDLE_MS = 400;    // at Stop: a live source that has read nothing for this long has nothing queued either
    var DRAIN_CHECK_MS = 100;   // how often the drain looks
    var DRAIN_LATE_MS = 250;    // a look this much later than asked for means the thread was blocked — frames may be waiting
    // Soft limiter for the 2-source sum (mic + system), replacing the WebAudio
    // DynamicsCompressor the mixer used. Knee −3 dBFS, ceiling −0.9 dBFS: the
    // ceiling is BELOW full scale on purpose — AAC overshoots on decode, and a
    // limiter that stops exactly at 1.0 still hands the encoder material that
    // comes back clipped (measured: ceiling 1.0 → +0.53 dBFS decoded).
    var LIM_KNEE = 0.7, LIM_CEIL = 0.9;

    function raceTimeout(p, ms) {
        return new Promise(function (resolve) {
            var id = setTimeout(function () { resolve({ timedOut: true }); }, ms);
            Promise.resolve(p).then(function () { clearTimeout(id); resolve({ ok: true }); }, function () { clearTimeout(id); resolve({ ok: false }); });
        });
    }
    function median(a) { var s = a.slice().sort(function (x, y) { return x - y; }); return s.length ? s[Math.floor(s.length / 2)] : 0; }
    function clamp(v, lo, hi) { return v < lo ? lo : (v > hi ? hi : v); }
    function softLimit(x) {
        var a = x < 0 ? -x : x;
        if (a <= LIM_KNEE) return x;
        var y = LIM_KNEE + (LIM_CEIL - LIM_KNEE) * Math.tanh((a - LIM_KNEE) / (LIM_CEIL - LIM_KNEE));
        return x < 0 ? -y : y;
    }
    // Blackman-windowed sinc, `cut` = cutoff relative to the OUTPUT Nyquist
    // (1 when upsampling / equal rates; SR/rate when downsampling).
    function buildSinc(cut) {
        var t = new Float32Array(SINC_PH * TAPS), half = TAPS / 2;
        for (var p = 0; p < SINC_PH; p++) {
            var ph = p / SINC_PH, sum = 0, base = p * TAPS;
            for (var k = 0; k < TAPS; k++) {
                var x = (k - (half - 1)) - ph;                 // tap offsets −7..+8 minus phase
                var xs = x * cut;
                var s = xs === 0 ? 1 : Math.sin(Math.PI * xs) / (Math.PI * xs);
                var w = 0.42 + 0.5 * Math.cos(Math.PI * x / half) + 0.08 * Math.cos(2 * Math.PI * x / half);
                var c = s * w * cut;
                t[base + k] = c; sum += c;
            }
            for (k = 0; k < TAPS; k++) t[base + k] /= sum;     // unity DC gain at every phase
        }
        return t;
    }

    function create(opts) {
        opts = opts || {};
        var SR = opts.sampleRate || 48000;
        var OUT_CH = opts.channels || 2;
        var onEmit = typeof opts.onEmit === 'function' ? opts.onEmit : function () {};
        var tel = typeof opts.telemetry === 'function' ? opts.telemetry : function () {};
        var RING = SR * RING_SEC;
        var ring = [];
        for (var c = 0; c < OUT_CH; c++) ring.push(new Float32Array(RING));
        var emitHead = 0;               // absolute output sample index already emitted
        var started = false, stopped = false, aborted = false;
        var drainTo = null;             // set by endAt()/stop(): frames captured before it are still the take
        var drainT0 = 0;                // when it was set, performance.now() ms
        var drain = { ms: 0, cut: false };
        var T0Ms = 0;                   // file zero, performance.now() ms
        var pauses = [];                // {s, e|null} in performance.now() ms
        var subscribers = [];
        var monoRing = new Float32Array(4096), monoHead = 0;
        var sources = [];
        var sincUnity = buildSinc(1);
        var hasTwo = (opts.sources || []).length >= 2;

        var gains = { sys: { gain: { value: 1 } }, mic: { gain: { value: 1 } } };

        function makeSource(def) {
            var s = {
                kind: def.kind, track: def.track, reader: null, loopDone: null,
                rate: 0, ch: 0, frames: 0, cal: [], originMs: null, domain: null,
                pre: [], head: null, buf: null, pos: 0, step: 1, eps: 0, sinc: sincUnity,
                gapUs: 0, gapEvents: 0, overlapUs: 0, lateSamples: 0, pauseDroppedUs: 0, preStartDroppedUs: 0,
                drainedUs: 0, readLagMaxMs: 0, lastReadMs: 0, drainIdle: false,
                lastArrival: 0, ended: false,
                dn: null, dnOn: !!def.denoise
            };
            if (def.gain != null) gains[def.kind].gain.value = def.gain;
            return s;
        }
        var DN_GAP_FILL = SR * 2;               // a hole this short is fed to the network as silence
        var STEMS = !!opts.stems && typeof opts.onStems === 'function';
        var stemRing = STEMS ? { sys: [], mic: [new Float32Array(RING)] } : null;
        if (STEMS) for (var sc = 0; sc < OUT_CH; sc++) stemRing.sys.push(new Float32Array(RING));
        // The network's stream for a source: `base` — the output index of its
        // first sample, `fed` / `out` — samples given / returned so far.
        function dnOpen(s, at) {
            s.dnOpens = (s.dnOpens || 0) + 1;
            try { s.dn = { st: window.RNNoiseProcessor.openStream(1.0), base: at, fed: 0, out: 0 }; }
            catch (e) { s.dn = null; s.dnOn = false; tel('record_mic_denoise_failed', { msg: String(e && e.message || e).slice(0, 80) }); }
        }
        function dnWrite(s, out) {
            var g = gains[s.kind].gain.value, from = s.dn.base + s.dn.out;
            for (var j = 0; j < out.length; j++) {
                var at = from + j;
                if (at < emitHead) { s.lateSamples++; continue; }
                var idx = at % RING, v = g * out[j];
                for (var oc = 0; oc < OUT_CH; oc++) ring[oc][idx] += v;
                if (STEMS) stemRing.mic[0][idx] = v;
            }
            s.dn.out += out.length;
        }
        function dnFeed(s, at, mono) {
            if (!s.dn) dnOpen(s, at);
            if (!s.dn) return false;
            var expect = s.dn.base + s.dn.fed;
            if (at !== expect) {
                var gap = at - expect;
                s.dnJumps = (s.dnJumps || 0) + 1; s.dnJumpMin = Math.min(s.dnJumpMin || 0, gap); s.dnJumpMax = Math.max(s.dnJumpMax || 0, gap);
                if (gap > 0 && gap <= DN_GAP_FILL) { dnWrite(s, s.dn.st.process(new Float32Array(gap))); s.dn.fed += gap; }
                else { dnWrite(s, s.dn.st.finish()); dnOpen(s, at); if (!s.dn) return false; }
            }
            dnWrite(s, s.dn.st.process(mono));
            s.dn.fed += mono.length;
            return true;
        }
        function dnFinish(s) {
            if (!s.dn) return;
            try { dnWrite(s, s.dn.st.finish()); } catch (_) {}
            s.dn = null;
        }
        // Where a source's samples are final: the network lags its resampler.
        function readyHead(s) { return s.dn ? s.dn.base + s.dn.out : s.head; }
        (opts.sources || []).forEach(function (d) { if (d && d.track) sources.push(makeSource(d)); });

        // ── clock bridge ────────────────────────────────────────────────
        // (timestamp − arrival) is constant per source. A perf-domain source
        // sits within a frame-length of zero; a TimeTicks source is hours
        // away. The MAX over the calibration frames is the least-delayed
        // read and therefore the truest constant (a late read can only make
        // the difference smaller).
        function finalizeOrigin(s, frameMs) {
            var m = median(s.cal), mx = -Infinity;
            for (var i = 0; i < s.cal.length; i++) if (s.cal[i] > mx) mx = s.cal[i];
            if (Math.abs(m) < 500) { s.domain = 'perf'; s.originMs = 0; }
            else { s.domain = 'ticks'; s.originMs = mx + frameMs + 1; }   // ts marks frame START; arrives ~1 ms after frame end
        }

        function pausedBeforeMs(t) {
            var acc = 0;
            for (var i = 0; i < pauses.length; i++) { var p = pauses[i]; if (p.e !== null && p.e <= t) acc += p.e - p.s; }
            return acc;
        }
        function insidePause(t) {
            for (var i = 0; i < pauses.length; i++) { var p = pauses[i]; if (t >= p.s && (p.e === null || t < p.e)) return true; }
            return false;
        }

        // ── deposit ─────────────────────────────────────────────────────
        function anchor(s, tsIndex) {
            s.head = Math.round(tsIndex);
            s.buf = [];
            for (var c = 0; c < s.ch; c++) s.buf.push(new Float32Array(TAPS / 2 - 1)); // zero left-context
            s.pos = TAPS / 2 - 1;
        }
        function appendInput(s, planes, off, len) {
            for (var c = 0; c < s.ch; c++) {
                var old = s.buf[c], nb = new Float32Array(old.length + len);
                nb.set(old, 0); nb.set(planes[c].subarray(off, off + len), old.length);
                s.buf[c] = nb;
            }
        }
        function processSource(s) {
            var L = s.buf[0].length, pos = s.pos, step = s.step, tbl = s.sinc, half = TAPS / 2;
            var g = gains[s.kind].gain.value;
            var srcCh = s.ch;
            if (s.dnOn && window.RNNoiseProcessor) {
                // mono at 48 kHz on the file clock → the network → the ring
                var stage = [], at0 = s.head;
                while (true) {
                    var i2 = Math.floor(pos);
                    if (i2 + half >= L) break;
                    var p2 = Math.round((pos - i2) * (SINC_PH - 1)) * TAPS, b2 = i2 - (half - 1), m = 0;
                    for (var c2 = 0; c2 < srcCh; c2++) { var bb = s.buf[c2], a2 = 0; for (var k2 = 0; k2 < TAPS; k2++) a2 += tbl[p2 + k2] * bb[b2 + k2]; m += a2; }
                    stage.push(m / srcCh);
                    s.head++; pos += step;
                }
                if (stage.length && !dnFeed(s, at0, Float32Array.from(stage))) {
                    // the network failed to open: the samples go as they are
                    for (var q = 0; q < stage.length; q++) {
                        var at = at0 + q; if (at < emitHead) { s.lateSamples++; continue; }
                        for (var oq = 0; oq < OUT_CH; oq++) ring[oq][at % RING] += g * stage[q];
                        if (STEMS) stemRing.mic[0][at % RING] = g * stage[q];
                    }
                }
                var keep2 = Math.floor(pos) - (half - 1);
                if (keep2 > 0) { for (var c3 = 0; c3 < srcCh; c3++) s.buf[c3] = s.buf[c3].slice(keep2); pos -= keep2; }
                s.pos = pos;
                return;
            }
            var stem = STEMS ? stemRing[s.kind] : null;
            while (true) {
                var i = Math.floor(pos);
                if (i + half >= L) break;
                var pIdx = Math.round((pos - i) * (SINC_PH - 1)) * TAPS;
                var base = i - (half - 1);
                if (s.head >= emitHead) {
                    var idx = s.head % RING;
                    for (var oc = 0; oc < OUT_CH; oc++) {
                        var b = s.buf[oc < srcCh ? oc : srcCh - 1], acc = 0;
                        for (var k = 0; k < TAPS; k++) acc += tbl[pIdx + k] * b[base + k];
                        ring[oc][idx] += g * acc;
                        if (stem) { if (stem.length === 1) { if (oc === 0) stem[0][idx] = 0; stem[0][idx] += g * acc / OUT_CH; } else stem[oc][idx] = g * acc; }
                    }
                } else s.lateSamples++;
                s.head++; pos += step;
            }
            var keepFrom = Math.floor(pos) - (half - 1);
            if (keepFrom > 0) { for (var c = 0; c < srcCh; c++) s.buf[c] = s.buf[c].slice(keepFrom); pos -= keepFrom; }
            s.pos = pos;
        }
        function flushTail(s) {
            // pad with zeros so the samples still waiting for right-context get out
            var pad = [];
            for (var c = 0; c < s.ch; c++) pad.push(new Float32Array(TAPS));
            appendInput(s, pad, 0, TAPS);
            processSource(s);
        }
        function depositSegment(s, fileUs, planes, off, len, rate) {
            if (len <= 0) return;
            var tsIndex = fileUs * SR / 1e6;
            if (s.head === null) { anchor(s, tsIndex); }
            else {
                var expected = s.head + (s.buf[0].length - s.pos) / s.step;
                var e = tsIndex - expected;                       // output samples
                var hard = HARD_MS * SR / 1000;
                if (e > hard) {
                    flushTail(s);
                    var gap = tsIndex - s.head;
                    // the network keeps its state across a short hole (dnFeed fills it)
                    s.gapUs += gap / SR * 1e6; s.gapEvents++;
                    tel('record_audio_gap_filled', { gapMs: Math.round(gap / SR * 1000), totalFilledMs: Math.round(s.gapUs / 1000), source: s.kind });
                    anchor(s, tsIndex);
                    e = 0;
                } else if (e < -hard) {
                    var dropIn = Math.round(-e * rate / SR);
                    if (dropIn >= len) { s.overlapUs += len / rate * 1e6; return; }
                    off += dropIn; len -= dropIn; s.overlapUs += dropIn / rate * 1e6;
                    e = 0;
                }
                s.eps = clamp(e / (SR * SERVO_S), -MAX_RATIO_DEV, MAX_RATIO_DEV);
                s.step = (rate / SR) / (1 + s.eps);
            }
            appendInput(s, planes, off, len);
            processSource(s);
        }
        // Split a frame at T0 and at pause boundaries; keep only what is recorded.
        function deposit(s, tPerfMs, planes, n, rate) {
            var durMs = n / rate * 1000;
            var bounds = [T0Ms];
            for (var i = 0; i < pauses.length; i++) { bounds.push(pauses[i].s); if (pauses[i].e !== null) bounds.push(pauses[i].e); }
            bounds = bounds.filter(function (b) { return b > tPerfMs && b < tPerfMs + durMs; }).sort(function (a, b) { return a - b; });
            var cuts = [tPerfMs].concat(bounds).concat([tPerfMs + durMs]);
            for (var j = 0; j + 1 < cuts.length; j++) {
                var a = cuts[j], b = cuts[j + 1];
                var off = Math.round((a - tPerfMs) / 1000 * rate), end = Math.round((b - tPerfMs) / 1000 * rate);
                if (end > n) end = n;
                var len = end - off;
                if (len <= 0) continue;
                if (a < T0Ms) { s.preStartDroppedUs += len / rate * 1e6; continue; }
                if (insidePause(a)) { s.pauseDroppedUs += len / rate * 1e6; continue; }
                var fileUs = (a - T0Ms) * 1000 - pausedBeforeMs(a) * 1000;
                depositSegment(s, fileUs, planes, off, len, rate);
            }
        }

        // ── emission ────────────────────────────────────────────────────
        function emit(final) {
            var nowMs = performance.now();
            var target = Infinity, maxHead = 0, any = false;
            for (var i = 0; i < sources.length; i++) {
                var s = sources[i];
                if (s.head === null) { if (!final && !s.ended && (nowMs - s.lastArrival) < ALIVE_MS && s.lastArrival) target = 0; continue; }
                var h = readyHead(s);
                if (h > maxHead) maxHead = h;
                var alive = !s.ended && (nowMs - s.lastArrival) < ALIVE_MS;
                if (final || alive) { any = true; if (h < target) target = h; }
            }
            if (!any) target = maxHead;
            if (target === Infinity) target = 0;
            // a source too far ahead would wrap the ring — let it through
            if (maxHead - emitHead > RING - SR) target = Math.max(target, maxHead - (RING >> 1));
            while (target > emitHead) {
                var n = Math.min(target - emitHead, SR);           // ≤ 1 s per block
                var planes = [];
                for (var c = 0; c < OUT_CH; c++) {
                    var out = new Float32Array(n), r = ring[c];
                    for (var j = 0; j < n; j++) { var idx = (emitHead + j) % RING; var v = r[idx]; r[idx] = 0; out[j] = hasTwo ? softLimit(v) : v; }
                    planes.push(out);
                }
                for (j = 0; j < n; j++) { var m = 0; for (c = 0; c < OUT_CH; c++) m += planes[c][j]; monoRing[(monoHead + j) % monoRing.length] = m / OUT_CH; }
                monoHead += n;
                var startUs = Math.round(emitHead * 1e6 / SR);
                var stems = null;
                if (STEMS) {
                    stems = { sys: [], mic: [] };
                    ['sys', 'mic'].forEach(function (kd) {
                        for (var sc2 = 0; sc2 < stemRing[kd].length; sc2++) {
                            var so = new Float32Array(n), sr2 = stemRing[kd][sc2];
                            for (var j2 = 0; j2 < n; j2++) { var ix2 = (emitHead + j2) % RING; so[j2] = sr2[ix2]; sr2[ix2] = 0; }
                            stems[kd].push(so);
                        }
                    });
                }
                emitHead += n;
                try { onEmit(planes, n, startUs); } catch (_) {}
                if (stems) { try { opts.onStems(stems, n, startUs); } catch (_) {} }
                for (var k = 0; k < subscribers.length; k++) { try { subscribers[k](planes, n, SR); } catch (_) {} }
            }
        }

        // ── ingest ──────────────────────────────────────────────────────
        function toPlanes(f) {
            var n = f.numberOfFrames, ch = f.numberOfChannels, planes = [];
            try {
                for (var c = 0; c < ch; c++) { var a = new Float32Array(n); f.copyTo(a, { planeIndex: c, format: 'f32-planar' }); planes.push(a); }
            } catch (_) {
                // exotic source format: read interleaved and split
                var inter = new Float32Array(n * ch); f.copyTo(inter, { planeIndex: 0, format: 'f32' });
                planes = [];
                for (c = 0; c < ch; c++) { var p = new Float32Array(n); for (var i = 0; i < n; i++) p[i] = inter[i * ch + c]; planes.push(p); }
            }
            return planes;
        }
        function ingest(s, f, arrivalMs) {
            var n = f.numberOfFrames, rate = f.sampleRate, ch = f.numberOfChannels;
            if (!n || !rate || !ch) return;
            var planes = toPlanes(f);
            var tsMs = f.timestamp / 1000;
            s.frames++; s.lastArrival = arrivalMs;
            if (s.rate !== rate || s.ch !== ch) {
                s.rate = rate; s.ch = ch;
                s.sinc = (rate > SR) ? buildSinc(SR / rate) : sincUnity;
                if (s.head !== null) { flushTail(s); dnFinish(s); s.head = null; }     // geometry changed: re-anchor on the next frame
            }
            if (s.cal.length < CAL_N) { s.cal.push(tsMs - arrivalMs); if (s.cal.length === CAL_N && s.originMs === null) finalizeOrigin(s, n / rate * 1000); }
            if (!started || s.originMs === null) {
                s.pre.push({ tsMs: tsMs, arrivalMs: arrivalMs, planes: planes, n: n, rate: rate });
                while (s.pre.length && arrivalMs - s.pre[0].arrivalMs > PRE_MS) s.pre.shift();
                return;
            }
            // how long the frame waited in the queue before the page read it:
            // how far behind a busy main thread let the audio fall
            var lagMs = arrivalMs - (tsMs - s.originMs + n / rate * 1000);
            if (lagMs > s.readLagMaxMs) s.readLagMaxMs = lagMs;
            deposit(s, tsMs - s.originMs, planes, n, rate);
            emit(false);
        }
        function drainPre(s) {
            if (s.originMs === null || !started) return;
            var pre = s.pre; s.pre = [];
            for (var i = 0; i < pre.length; i++) deposit(s, pre[i].tsMs - s.originMs, pre[i].planes, pre[i].n, pre[i].rate);
        }

        async function readLoop(s) {
            try {
                var proc = new MediaStreamTrackProcessor({ track: s.track, maxBufferSize: MAX_BUFFER });
                s.reader = proc.readable.getReader();
            } catch (e) { s.ended = true; tel('record_audio_source_unreadable', { source: s.kind, msg: (e && e.message || '').slice(0, 100) }); return; }
            while (!aborted) {
                var r;
                try { r = await s.reader.read(); } catch (_) { break; }
                if (r.done) break;
                var f = r.value;
                s.lastReadMs = performance.now();
                if (drainTo !== null) {
                    // Stop was pressed: what was captured before it is still the
                    // take — it only waited in the queue; the first frame captured
                    // after it ends this source.
                    var capMs = s.originMs !== null ? f.timestamp / 1000 - s.originMs : null;
                    if (capMs === null || capMs >= drainTo) { try { f.close(); } catch (_) {} break; }
                    s.drainedUs += f.numberOfFrames / f.sampleRate * 1e6;
                }
                try { if (!stopped) ingest(s, f, performance.now()); } catch (_) {}
                try { f.close(); } catch (_) {}
            }
            s.ended = true;
            if (started && !stopped && !aborted) emit(false);
        }
        sources.forEach(function (s) { s.loopDone = readLoop(s); });

        // ── public surface (mirrors buildMixer's shape) ─────────────────
        var api = {
            ctx: null, dest: null, mixBus: null,
            sampleRate: SR, channels: OUT_CH,
            sysGain: gains.sys, micGain: gains.mic,
            analyser: {
                fftSize: 1024,
                getFloatTimeDomainData: function (buf) {
                    var n = Math.min(buf.length, monoRing.length);
                    for (var i = 0; i < n; i++) buf[i] = monoRing[(monoHead - n + i + monoRing.length * 4) % monoRing.length];
                    for (; i < buf.length; i++) buf[i] = 0;
                }
            },
            sourceCount: sources.length,
            start: function (t0Ms) {
                if (started) return;
                T0Ms = (t0Ms != null) ? t0Ms : performance.now();
                started = true;
                sources.forEach(function (s) {
                    if (s.originMs === null && s.cal.length >= 3) finalizeOrigin(s, s.pre.length ? s.pre[s.pre.length - 1].n / s.pre[s.pre.length - 1].rate * 1000 : 10);
                    drainPre(s);
                });
                emit(false);
            },
            pause: function (tMs) { if (!pauses.length || pauses[pauses.length - 1].e !== null) pauses.push({ s: (tMs != null ? tMs : performance.now()), e: null }); },
            resume: function (tMs) { var p = pauses[pauses.length - 1]; if (p && p.e === null) p.e = (tMs != null ? tMs : performance.now()); },
            subscribe: function (fn) { if (typeof fn === 'function') subscribers.push(fn); return function () { var i = subscribers.indexOf(fn); if (i >= 0) subscribers.splice(i, 1); }; },
            stats: function () {
                var out = { sampleRate: SR, emittedMs: Math.round(emitHead / SR * 1000), gapMs: 0, gapEvents: 0, overlapMs: 0, lateMs: 0,
                    drainedMs: 0, drainMs: drain.ms, drainCut: drain.cut, drainIdle: 0, readLagMaxMs: 0, sources: [] };
                sources.forEach(function (s) {
                    out.gapMs += Math.round(s.gapUs / 1000); out.gapEvents += s.gapEvents; out.overlapMs += Math.round(s.overlapUs / 1000); out.lateMs += Math.round(s.lateSamples / SR * 1000);
                    out.drainedMs = Math.max(out.drainedMs, Math.round(s.drainedUs / 1000));
                    out.readLagMaxMs = Math.max(out.readLagMaxMs, Math.round(s.readLagMaxMs));
                    if (s.drainIdle) out.drainIdle++;
                    out.sources.push({ kind: s.kind, denoise: s.dnOn ? 1 : 0, dnOpens: s.dnOpens || 0, dnJumps: s.dnJumps || 0, dnJumpMin: s.dnJumpMin || 0, dnJumpMax: s.dnJumpMax || 0, rate: s.rate, ch: s.ch, frames: s.frames, domain: s.domain, originMs: s.originMs === null ? null : Math.round(s.originMs), gapMs: Math.round(s.gapUs / 1000), gapEvents: s.gapEvents, overlapMs: Math.round(s.overlapUs / 1000), lateMs: Math.round(s.lateSamples / SR * 1000), pauseDroppedMs: Math.round(s.pauseDroppedUs / 1000), epsPpm: Math.round(s.eps * 1e6), ended: s.ended, drainedMs: Math.round(s.drainedUs / 1000), readLagMaxMs: Math.round(s.readLagMaxMs), drainIdle: s.drainIdle });
                });
                return out;
            },
            // endAt(tMs) — the take ends at `tMs` (performance.now() ms, default
            // now). From here on the first frame a source captured at or after
            // it ends that source, and what it captured before it — on a busy
            // page still waiting in the processors' queues — is the take. The
            // recorders call this the instant Stop is pressed: the audio tracks
            // stay live until stop() has read their queues, and without the mark
            // the steps before it would take in sound from after the click.
            endAt: function (tMs) {
                if (drainTo !== null || !started || stopped || aborted) return;
                drainTo = (tMs != null) ? tMs : performance.now();
                drainT0 = performance.now();
            },
            // stop(atMs, {wait}) — read what the sources captured before `atMs`
            // (endAt, if not marked already), then cut. The caller stops the
            // audio TRACKS only after this resolves — track.stop() discards a
            // processor's queue, and on a busy page seconds of the take's end
            // wait in it (measured 26.09.2026: a 2 s backlog → 0 frames readable
            // after track.stop(), all 200 read back in 19 ms from the live
            // track; the tool on a busy machine kept 3.75 s of sound of a 6.2 s
            // take; prod, 30 d: of 1965 MP4 takes with sound and no pause, 22
            // (1.1 %) ended 0.5–4.5 s short — the reach of the 4 s queue).
            // A source is done on its first frame captured after the mark —
            // which a LIVE source that has stopped delivering never sends: tab
            // and system audio go quiet in silence (prod, 30 d: system-audio
            // gaps up to 17.9 s; 30 of those 1965 takes had sound that ended
            // more than 4.5 s before the picture), and each such Stop waited the
            // whole DRAIN_MAX_MS (measured 27.09.2026: 5.0 s against 49 ms with
            // the source live). A source that has read nothing for DRAIN_IDLE_MS
            // has nothing queued either — reading a queue runs at hundreds of
            // frames per 20 ms — and is ended. `wait(p, ms)` is the caller's
            // bounded wait, on a clock a hidden tab does not throttle.
            stop: async function (atMs, o) {
                o = o || {};
                if (started && !stopped && !aborted) {
                    api.endAt(atMs);
                    // a source that delivers nothing has nothing queued to wait for
                    sources.forEach(function (s) {
                        if (s.reader && (!s.track || s.track.readyState !== 'live' || s.track.muted)) { try { s.reader.cancel().catch(function () {}); } catch (_) {} }
                    });
                    var all = Promise.all(sources.map(function (s) { return s.loopDone; }));
                    var wait = (typeof o.wait === 'function') ? o.wait : raceTimeout;
                    for (;;) {
                        var asked = performance.now();
                        var res = await wait(all, DRAIN_CHECK_MS);
                        if (!res || !res.timedOut) break;
                        var now = performance.now();
                        if (now - drainT0 >= DRAIN_MAX_MS) { drain.cut = true; break; }
                        // a late look means this thread was blocked, and frames
                        // may be waiting to be read — decide on the next one
                        if (now - asked > DRAIN_CHECK_MS + DRAIN_LATE_MS) continue;
                        sources.forEach(function (s) {
                            if (s.ended || !s.reader || s.drainIdle) return;
                            if (now - Math.max(s.lastReadMs, drainT0) < DRAIN_IDLE_MS) return;
                            s.drainIdle = true;
                            try { s.reader.cancel().catch(function () {}); } catch (_) {}
                        });
                    }
                    drain.ms = Math.round(performance.now() - drainT0);
                }
                stopped = true;
                for (var i = 0; i < sources.length; i++) { var s = sources[i]; try { if (s.reader) await s.reader.cancel(); } catch (_) {} }
                try { await Promise.all(sources.map(function (s) { return s.loopDone; })); } catch (_) {}
                if (started) { sources.forEach(function (s) { if (s.head !== null) { try { flushTail(s); } catch (_) {} } try { dnFinish(s); } catch (_) {} }); emit(true); }
                return api.stats();
            },
            abort: function () {
                aborted = true; stopped = true;
                sources.forEach(function (s) { try { if (s.reader) s.reader.cancel().catch(function () {}); } catch (_) {} });
            },
            close: function () { api.abort(); }
        };
        return api;
    }

    window.CaptureAudioTimeline = { create: create, _buildSinc: buildSinc };
})();
